import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import ZoomVideo from '@zoom/videosdk';

import { usePatientLoginMutation, useVerifyUserTokenMutation } from 'services/auth/auth';
import { PatientLoginResProps, VerifyUserTokenResProps } from 'services/auth/auth.types';
import { useLazyGetMembershipPlansQuery } from 'services/lookup/lookup';
import { useLazyGetMyAccountQuery } from 'services/myAccount/myAccount';
import { useLazyGetMyProgramsQuery } from 'services/programs/programs';
import { useLazyGetSurveysQuery } from 'services/surveys/surveys';

import { selectUser } from 'store';
import { clearUser, setToken, setUser } from 'store/user/userSlice';

import Loader from 'shared/Loader';
import { getFlowByUrl } from 'pages/Login/login.settings';
import { isSignupInProgress } from 'utils/onboarding';

import { ZoomChatSlot } from 'features/ZoomChat';

import { useAppDispatch, useAppSelector, useQuery } from 'hooks';
import { useAnalyticsManager } from 'hooks/useAnalyticsManager/useAnalyticsManager';
import { useAppAnalytics } from 'hooks/useAppAnalytics/useAppAnalytics';
import { useDarkTheme } from 'hooks/useDarkTheme';
import { useDeepLinks } from 'hooks/useDeepLinks/useDeepLinks';
import { useMarketingParams } from 'hooks/useMarketingParams';
import { useRemoveQueryParams } from 'hooks/useRemoveQueryParams/useRemoveQueryParams';
import { useRouteMatch } from 'hooks/useRouteMatch';
import { useNotificationsCount } from 'hooks/useNotificationsCount/useNotificationsCount';
import { useSocketLifecycle } from 'hooks/useSocketLifecycle/useSocketLifecycle';
import { useUnleashContextHook } from 'hooks/useUnleashContext';
import { PathName } from 'router/paths';
import { selectSessionScope } from 'utils/authSession';
import { handleRequestCatch } from 'utils/error';
import { hasWeightManagementProgram } from 'utils/programs';
import { local, LOCAL_KEYS, session, SESSION_KEYS } from 'utils/storage';

import { UserProgram } from './models/programs.types';
import { FeatureStatusType } from './models/user.types';
import { notifyError } from './shared/Toast/Toast';
import AppRouter from './router';

const App = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const { accessToken, userId } = useAppSelector(selectUser);
  const sessionScope = useAppSelector(selectSessionScope);
  const [patientLogin, { isLoading }] = usePatientLoginMutation();
  const [getMyAccount] = useLazyGetMyAccountQuery();
  const [getMyPrograms] = useLazyGetMyProgramsQuery();
  const [getSurveys] = useLazyGetSurveysQuery();
  const [getToken] = useVerifyUserTokenMutation();
  const [getMembershipPlans] = useLazyGetMembershipPlansQuery();

  const dispatch = useAppDispatch();
  const query = useQuery();
  const navigate = useNavigate();
  const [, removeQueryParams] = useRemoveQueryParams();
  const { handleDeepLinks } = useDeepLinks();

  const isCreateNewPassword = useRouteMatch(PathName.CreateNewPassword);
  const isLoginPage = useRouteMatch(PathName.Login);
  const isHomePage = useRouteMatch(PathName.Home)?.isExact;
  const showOnlyLoader = isLoading || initialLoading || (accessToken && !userId);

  /**
   * Evaluates pending deep links once the user's programs are loaded and replaces
   * the current history entry to avoid duplicating navigation steps.
   */
  const handleGetProgramsThen = async (data: UserProgram[], storeStatus?: FeatureStatusType) => {
    const urlToNavigate = await handleDeepLinks(data, true, storeStatus);
    if (urlToNavigate) {
      navigate(urlToNavigate, { replace: true });
    }
  };

  // use for 'sign in as patient' functionality
  const handlePatientLoginThen = async (data: PatientLoginResProps) => {
    local.set(LOCAL_KEYS.LOGOUT_EVENT, 'logout' + Math.random());
    const createAppointment = query.get('create-appointment');
    const staffId = query.get('staffId'); //
    // Marked before the patient reaches the store: the first render for them already has to
    // know it is staff, or the AI chat shows while the account loads (LMD-1820).
    session.set(SESSION_KEYS.VIEW_AS_PATIENT, 'true');
    dispatch(setUser({ ...data.data, profileImage: '' })); // * provider won't see profile image of patient
    await Promise.allSettled([
      getMyAccount().unwrap(),
      getMyPrograms().unwrap(),
      getMembershipPlans().unwrap()
    ]);
    // A `clearUser` that lands during the awaits (a 401 whose renewal failed, another tab's
    // logout) ends this session and drops the marker with it; its staff id must not outlive it.
    staffId &&
      session.get(SESSION_KEYS.VIEW_AS_PATIENT) &&
      session.set(SESSION_KEYS.STAFF_ID, staffId);
    navigate(
      createAppointment && JSON.parse(createAppointment)
        ? PathName.CreateAppointment
        : PathName.Home
    );
  };

  const { trackLogin } = useAnalyticsManager();

  const handleGetTokenThen = (res: VerifyUserTokenResProps) => {
    dispatch(clearUser());
    dispatch(setToken({ accessToken: res.data.accessToken, refreshToken: res.data.refreshToken }));
    // Token-based patient login (magic-link / deep-link). `handlePatientLoginThen` above is the
    // staff sign-in-as-patient flow and is intentionally NOT tracked — it's an internal tool,
    // not a patient login.
    trackLogin({ kind: 'temporary_token', method: 'token' });
  };

  const handleQueryParams = () => {
    const handleLogoutEvent = (event: StorageEvent) => {
      if (event.key == 'logout-event') {
        ZoomVideo?.destroyClient();
        dispatch(clearUser());
      }
    };
    window.addEventListener('storage', handleLogoutEvent);

    const run = async () => {
      try {
        const accessTokenFromQuery = query.get('accessToken');
        if (accessTokenFromQuery) {
          dispatch(clearUser());
          dispatch(setUser({ accessToken: accessTokenFromQuery }));
          // Auto-login from a deep link that already carries an `accessToken`. Same patient
          // login pathway as `verifyUserToken` — just without the backend round-trip — so we
          // report the same `method: 'token'`, and `kind` is what separates the two.
          trackLogin({ kind: 'access_token', method: 'token' });
        }

        const temporaryToken = query.get('token');
        if (temporaryToken) {
          if (isCreateNewPassword) {
            dispatch(clearUser());
            return;
          }
          if (accessToken) return;
          removeQueryParams('token');
          getToken({ disableActiveUsers: false, token: temporaryToken })
            .unwrap()
            .then(handleGetTokenThen)
            .catch((err) => {
              if (err.status === 401) {
                dispatch(clearUser());
              }
            });
        }

        const refetch = query.get('refetch') === 'true';
        if (refetch) {
          getMyAccount();
        }

        const loginToken = query.get('login-token');
        if (loginToken) {
          dispatch(clearUser());
          patientLogin({ loginToken, staffId: query.get('staffId') ?? '' })
            .unwrap()
            .then(handlePatientLoginThen)
            .catch(handleRequestCatch);
        }
        if (!accessToken && !accessTokenFromQuery && !query.get('token') && query.get('action')) {
          handleDeepLinks().then((urlToNavigate) => {
            if (urlToNavigate) {
              navigate(urlToNavigate);
            }
          });
        }
      } finally {
        setInitialLoading(false);
      }
    };

    run();

    return () => window.removeEventListener('storage', handleLogoutEvent);
  };

  useDarkTheme();
  useSocketLifecycle();
  useNotificationsCount();
  useAppAnalytics();
  useMarketingParams();

  useEffect(handleQueryParams, []);

  useEffect(() => {
    const handleUpdatedAccessToken = async () => {
      const loginToken = query.get('login-token');
      if (accessToken && !loginToken) {
        query.has('action') && setInitialLoading(true);
        try {
          // Account hydration must finish even when optional dashboard data fails.
          const [accountResult, programsResult] = await Promise.allSettled([
            getMyAccount().unwrap(),
            getMyPrograms().unwrap(),
            getMembershipPlans().unwrap()
          ]);
          const account =
            accountResult.status === 'fulfilled' ? accountResult.value.data : undefined;
          const programData =
            programsResult.status === 'fulfilled' ? (programsResult.value ?? []) : [];
          if (account && isSignupInProgress(account)) {
            if (query.has('action') || isLoginPage || isHomePage) {
              // From the response just awaited rather than the store, which `setPrograms` merges
              // into and never prunes — on this branch it can still be carrying the programs of
              // whoever signed in before.
              navigate(
                getFlowByUrl(account.flowUrl ?? '', {
                  hadOrHasWeightManagement: hasWeightManagementProgram(programData)
                }),
                { replace: true }
              );
            }
            return;
          }
          if (programData.length > 0) {
            await getSurveys().unwrap().catch(handleRequestCatch);
          }
          await handleGetProgramsThen(programData, account?.features?.store?.status);
        } catch (e) {
          notifyError((e as Error).message ?? 'Error fetching data');
        } finally {
          setInitialLoading(false);
        }
      }
    };
    handleUpdatedAccessToken();
    // Per session, not per token: a login-service renewal must not reload everything (LMD-682).
  }, [sessionScope]);

  useUnleashContextHook();

  return (
    <>
      <ToastContainer className="min-w-full md:min-w-[45%]" position="top-center" />
      {showOnlyLoader ? (
        <Loader isVisible />
      ) : (
        <>
          <AppRouter />
          {/* Beside the router and not inside its keyed routes, so the Zoom chat and its identity
              hook are not torn down and rebuilt on every page change. Inside the loader branch,
              not outside it: the loader shows during sign-in and the token swap, when Zoom must
              not be injected yet. */}
          <ZoomChatSlot />
        </>
      )}
    </>
  );
};

export default App;
